import React, {useState, useCallback, useRef} from 'react'
import {Editor} from '@tiptap/react'
import Popover from '@material-ui/core/Popover'
import Tooltip from '@material-ui/core/Tooltip'
import IconButton from '@material-ui/core/IconButton'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'
import {
  selectColumn,
  selectRow,
  mergeCellInDirection,
  canMergeCellInDirection,
  isHeaderRowActive,
  isHeaderColumnActive,
} from './tableHelpers'

type TableContextToolbarProps = {
  editor: Editor
}

type MenuType = 'column' | 'row' | 'merge' | null

// --- Inline SVG Icons ---

function ColumnIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.2"
    >
      <rect x="1" y="1" width="14" height="14" rx="1" />
      <line x1="5.5" y1="1" x2="5.5" y2="15" />
      <line x1="10.5" y1="1" x2="10.5" y2="15" />
    </svg>
  )
}

function RowIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.2"
    >
      <rect x="1" y="1" width="14" height="14" rx="1" />
      <line x1="1" y1="5.5" x2="15" y2="5.5" />
      <line x1="1" y1="10.5" x2="15" y2="10.5" />
    </svg>
  )
}

function MergeIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.2"
    >
      <rect x="1" y="1" width="14" height="14" rx="1" />
      <line x1="8" y1="1" x2="8" y2="8" />
      <line x1="1" y1="8" x2="15" y2="8" />
    </svg>
  )
}

function ChevronDown() {
  return (
    <svg width="8" height="8" viewBox="0 0 8 8" fill="currentColor">
      <path
        d="M1 2.5L4 5.5L7 2.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      />
    </svg>
  )
}

// --- Toggle Switch ---

function ToggleSwitch({active}: {active: boolean}) {
  return (
    <span
      className={`html-editor-table-dropdown__toggle${
        active ? ' html-editor-table-dropdown__toggle--active' : ''
      }`}
    />
  )
}

// --- Menu Item ---

type MenuItemProps = {
  label: string
  onClick: () => void
  disabled?: boolean
  toggle?: boolean
  toggleActive?: boolean
}

function MenuItem({
  label,
  onClick,
  disabled,
  toggle,
  toggleActive,
}: MenuItemProps) {
  return (
    <button
      type="button"
      className={`html-editor-table-dropdown__item${
        disabled ? ' html-editor-table-dropdown__item--disabled' : ''
      }`}
      onClick={() => {
        if (!disabled) onClick()
      }}
      disabled={disabled}
    >
      <span>{label}</span>
      {toggle && <ToggleSwitch active={!!toggleActive} />}
    </button>
  )
}

// --- Main Component ---

export default function TableContextToolbar({
  editor,
}: TableContextToolbarProps) {
  const [openMenu, setOpenMenu] = useState<MenuType>(null)
  const columnRef = useRef<HTMLButtonElement>(null)
  const rowRef = useRef<HTMLButtonElement>(null)
  const mergeRef = useRef<HTMLButtonElement>(null)

  const toggleMenu = useCallback((menu: MenuType) => {
    setOpenMenu((prev) => (prev === menu ? null : menu))
  }, [])

  const closeMenu = useCallback(() => {
    setOpenMenu(null)
  }, [])

  const runAndClose = useCallback(
    (fn: () => void) => {
      fn()
      closeMenu()
    },
    [closeMenu],
  )

  const headerRowActive = isHeaderRowActive(editor)
  const headerColumnActive = isHeaderColumnActive(editor)

  const canMergeStandard = editor.can().mergeCells()
  const canSplit = editor.can().splitCell()
  const canMergeUp = canMergeCellInDirection(editor, 'up')
  const canMergeRight = canMergeCellInDirection(editor, 'right')
  const canMergeDown = canMergeCellInDirection(editor, 'down')
  const canMergeLeft = canMergeCellInDirection(editor, 'left')

  return (
    <div className="html-editor-table-toolbar">
      {/* Column */}
      <button
        ref={columnRef}
        type="button"
        className={`html-editor-table-toolbar__button${
          openMenu === 'column'
            ? ' html-editor-table-toolbar__button--active'
            : ''
        }`}
        onClick={() => toggleMenu('column')}
      >
        <ColumnIcon />
        <ChevronDown />
      </button>
      <Popover
        open={openMenu === 'column'}
        anchorEl={columnRef.current}
        onClose={closeMenu}
        anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
        transformOrigin={{vertical: 'top', horizontal: 'left'}}
        disableRestoreFocus
      >
        <div className="html-editor-table-dropdown">
          <MenuItem
            label="Header column"
            onClick={() =>
              runAndClose(() =>
                editor.chain().focus().toggleHeaderColumn().run(),
              )
            }
            toggle
            toggleActive={headerColumnActive}
          />
          <MenuItem
            label="Insert column left"
            onClick={() =>
              runAndClose(() => editor.chain().focus().addColumnBefore().run())
            }
          />
          <MenuItem
            label="Insert column right"
            onClick={() =>
              runAndClose(() => editor.chain().focus().addColumnAfter().run())
            }
          />
          <MenuItem
            label="Delete column"
            onClick={() =>
              runAndClose(() => editor.chain().focus().deleteColumn().run())
            }
          />
          <MenuItem
            label="Select column"
            onClick={() => runAndClose(() => selectColumn(editor))}
          />
        </div>
      </Popover>

      {/* Row */}
      <button
        ref={rowRef}
        type="button"
        className={`html-editor-table-toolbar__button${
          openMenu === 'row' ? ' html-editor-table-toolbar__button--active' : ''
        }`}
        onClick={() => toggleMenu('row')}
      >
        <RowIcon />
        <ChevronDown />
      </button>
      <Popover
        open={openMenu === 'row'}
        anchorEl={rowRef.current}
        onClose={closeMenu}
        anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
        transformOrigin={{vertical: 'top', horizontal: 'left'}}
        disableRestoreFocus
      >
        <div className="html-editor-table-dropdown">
          <MenuItem
            label="Header row"
            onClick={() =>
              runAndClose(() => editor.chain().focus().toggleHeaderRow().run())
            }
            toggle
            toggleActive={headerRowActive}
          />
          <MenuItem
            label="Insert row above"
            onClick={() =>
              runAndClose(() => editor.chain().focus().addRowBefore().run())
            }
          />
          <MenuItem
            label="Insert row below"
            onClick={() =>
              runAndClose(() => editor.chain().focus().addRowAfter().run())
            }
          />
          <MenuItem
            label="Delete row"
            onClick={() =>
              runAndClose(() => editor.chain().focus().deleteRow().run())
            }
          />
          <MenuItem
            label="Select row"
            onClick={() => runAndClose(() => selectRow(editor))}
          />
        </div>
      </Popover>

      {/* Merge */}
      <button
        ref={mergeRef}
        type="button"
        className={`html-editor-table-toolbar__button${
          openMenu === 'merge'
            ? ' html-editor-table-toolbar__button--active'
            : ''
        }`}
        onClick={() => toggleMenu('merge')}
      >
        <MergeIcon />
        <ChevronDown />
      </button>
      <Popover
        open={openMenu === 'merge'}
        anchorEl={mergeRef.current}
        onClose={closeMenu}
        anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
        transformOrigin={{vertical: 'top', horizontal: 'left'}}
        disableRestoreFocus
      >
        <div className="html-editor-table-dropdown">
          <MenuItem
            label="Merge cells"
            onClick={() =>
              runAndClose(() => editor.chain().focus().mergeCells().run())
            }
            disabled={!canMergeStandard}
          />
          <MenuItem
            label="Merge cell up"
            onClick={() =>
              runAndClose(() => mergeCellInDirection(editor, 'up'))
            }
            disabled={!canMergeUp}
          />
          <MenuItem
            label="Merge cell right"
            onClick={() =>
              runAndClose(() => mergeCellInDirection(editor, 'right'))
            }
            disabled={!canMergeRight}
          />
          <MenuItem
            label="Merge cell down"
            onClick={() =>
              runAndClose(() => mergeCellInDirection(editor, 'down'))
            }
            disabled={!canMergeDown}
          />
          <MenuItem
            label="Merge cell left"
            onClick={() =>
              runAndClose(() => mergeCellInDirection(editor, 'left'))
            }
            disabled={!canMergeLeft}
          />
          <MenuItem
            label="Split cell vertically"
            onClick={() =>
              runAndClose(() => editor.chain().focus().splitCell().run())
            }
            disabled={!canSplit}
          />
          <MenuItem
            label="Split cell horizontally"
            onClick={() =>
              runAndClose(() => editor.chain().focus().splitCell().run())
            }
            disabled={!canSplit}
          />
        </div>
      </Popover>

      {/* Delete Table */}
      <Tooltip title="Delete Table">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().deleteTable().run()}
          style={{marginLeft: 4}}
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </div>
  )
}
